/**
 * Platform insights KPI cards — contract with `GET /admin/platform-insights/kpis`
 * (aapastech-backend/src/routes/platform-kpis.ts) plus the copy and formatting
 * each card needs. Definitions follow YOM-ANALYTICS-TRACKING-PLAN.md §A1 and
 * say which Tyashin data the backend reads, so a superadmin can tell what a
 * number does and does not include.
 */

export type KpiKey =
  | 'wvs'
  | 'activation'
  | 'aha'
  | 'ttvPublish'
  | 'ttvLead'
  | 'retentionD1'
  | 'retentionD7'
  | 'retentionD30'
  | 'builderWau'
  | 'builderMau'
  | 'stickiness'
  | 'adoption'
  | 'signupToPaid'
  | 'aiCostPerActiveProject'
  | 'deploySuccess'
  | 'cwvPass';

export interface KpiSummary {
  key: KpiKey;
  kind: 'point' | 'ratio' | 'median';
  unit: 'count' | 'percent' | 'hours' | 'usd';
  value: number | null;
  previous: number | null;
  series: { day: string; value: number | null }[];
  detail: Record<string, unknown> | null;
  unavailable: string | null;
}

export interface PlatformKpis {
  range: { from: string; to: string };
  previous: { from: string; to: string };
  lastComputedAt: string | null;
  daysWithData: number;
  partialDays: string[];
  kpis: KpiSummary[];
}

export interface KpiRecomputeStats {
  daysComputed: number;
  daysWritten: number;
  errors: number;
  firstError?: string;
  timedOut: boolean;
}

export interface KpiMeta {
  label: string;
  definition: string;
  source: string;
  /** For TTV and cost a fall is the good direction. */
  lowerIsBetter?: boolean;
}

export const KPI_META: Record<KpiKey, KpiMeta> = {
  wvs: {
    label: 'Weekly value-delivering sites',
    definition:
      'Live sites that delivered at least one lead, chatbot lead or order to their owner in the 7 days to the end of the period.',
    source: 'Contact forms (D1), chatbot leads (CRM), orders',
  },
  activation: {
    label: 'Activation',
    definition:
      'Teams that published a site within 14 days of sign-up, by sign-up cohort. A visit is also required once YOM site traffic covers the window.',
    source: 'Teams, builds and deploys (+ YOM rollup when on)',
  },
  aha: {
    label: 'First lead within 30 days',
    definition: 'Teams whose site delivered a first lead or order within 30 days of sign-up.',
    source: 'Teams, contact forms, chatbot leads, orders',
  },
  ttvPublish: {
    label: 'Time to first published site',
    definition: 'Median time from sign-up to the team’s first successful publish.',
    source: 'Teams, build history',
    lowerIsBetter: true,
  },
  ttvLead: {
    label: 'Time to first lead',
    definition: 'Median time from sign-up to the first lead or order on any of the team’s sites.',
    source: 'Teams, contact forms, chatbot leads, orders',
    lowerIsBetter: true,
  },
  retentionD1: {
    label: 'Retention D1',
    definition: 'Teams active the day after sign-up.',
    source: 'Builder activity (AI jobs, Studio runs, deploys, edits, audit log)',
  },
  retentionD7: {
    label: 'Retention D7',
    definition: 'Teams active on days 7 to 13 after sign-up.',
    source: 'Builder activity',
  },
  retentionD30: {
    label: 'Retention D30',
    definition: 'Teams active on days 30 to 36 after sign-up.',
    source: 'Builder activity',
  },
  builderWau: {
    label: 'Builder WAU',
    definition: 'Distinct people (staff excluded) with builder activity in the last 7 days.',
    source: 'Builder activity',
  },
  builderMau: {
    label: 'Builder MAU',
    definition: 'Distinct people (staff excluded) with builder activity in the last 30 days.',
    source: 'Builder activity',
  },
  stickiness: {
    label: 'Stickiness',
    definition: 'Builder WAU divided by MAU.',
    source: 'Builder activity',
  },
  adoption: {
    label: 'Feature adoption',
    definition:
      'Live sites that used chatbot, blog, commerce, CRM or newsletter in the last 30 days. Per-feature bars show enabled and used.',
    source: 'Plugin configs, domains, chats, posts, orders, deals, campaigns',
  },
  signupToPaid: {
    label: 'Sign-up to paid',
    definition:
      'Teams with a paid invoice within 30 days of sign-up, by sign-up cohort. Gateway trials are not stored with their start, so every new team is the cohort.',
    source: 'Teams, billing invoices',
  },
  aiCostPerActiveProject: {
    label: 'AI cost per active project',
    definition:
      'LLM spend in the last 30 days divided by live projects with builder activity in that window.',
    source: 'ai_usage_log (token cost ledger)',
    lowerIsBetter: true,
  },
  deploySuccess: {
    label: 'Deploy success rate',
    definition: 'Successful builds and deploys over all finished ones.',
    source: 'Workers Builds jobs, static deploys',
  },
  cwvPass: {
    label: 'Storefront Core Web Vitals pass rate',
    definition:
      'Live sites whose p75 LCP ≤ 2.5 s, INP ≤ 200 ms and CLS ≤ 0.1 (metrics without data are not counted against a site).',
    source: 'YOM site rollup, else Google CrUX field data via SEO Co-Pilot',
  },
};

export const FEATURE_LABEL: Record<string, string> = {
  chatbot: 'Chatbot',
  blog: 'Blog',
  seo: 'SEO Co-Pilot',
  commerce: 'Commerce',
  crm: 'CRM',
  newsletter: 'Newsletter',
  custom_domain: 'Custom domain',
};

export function formatKpiValue(unit: KpiSummary['unit'], v: number | null): string {
  if (v == null || !Number.isFinite(v)) return '—';
  switch (unit) {
    case 'percent':
      return `${Math.round(v * 10) / 10}%`;
    case 'usd':
      return v < 1 ? `$${v.toFixed(3)}` : `$${v.toFixed(2)}`;
    case 'hours':
      if (v < 1) return `${Math.round(v * 60)} min`;
      if (v < 48) return `${Math.round(v * 10) / 10} h`;
      return `${Math.round((v / 24) * 10) / 10} days`;
    default:
      return new Intl.NumberFormat().format(Math.round(v));
  }
}

export type DeltaTone = 'good' | 'bad' | 'neutral';

/**
 * Change against the previous period. Percent units compare in points
 * (pp); everything else as a relative change. `null` when there is nothing
 * honest to compare against.
 */
export function kpiDelta(
  k: Pick<KpiSummary, 'unit' | 'value' | 'previous' | 'key'>
): { text: string; tone: DeltaTone; direction: 'up' | 'down' | 'flat' } | null {
  if (k.value == null || k.previous == null) return null;
  const diff = k.value - k.previous;
  const direction = Math.abs(diff) < 1e-9 ? 'flat' : diff > 0 ? 'up' : 'down';
  let text: string;
  if (k.unit === 'percent') {
    text = `${diff > 0 ? '+' : ''}${Math.round(diff * 10) / 10} pp`;
  } else if (k.previous === 0) {
    if (direction === 'flat') text = '0%';
    else return { text: 'new', tone: 'neutral', direction };
  } else {
    const pct = (diff / Math.abs(k.previous)) * 100;
    text = `${pct > 0 ? '+' : ''}${Math.round(pct)}%`;
  }
  if (direction === 'flat') return { text, tone: 'neutral', direction };
  const better = KPI_META[k.key].lowerIsBetter ? direction === 'down' : direction === 'up';
  return { text, tone: better ? 'good' : 'bad', direction };
}

/** Points for a sparkline: null days break the line rather than drop to 0. */
export function sparklinePath(
  series: { value: number | null }[],
  width: number,
  height: number
): string {
  const vals = series.map((p) => p.value);
  const nums = vals.filter((v): v is number => v != null && Number.isFinite(v));
  if (nums.length < 2) return '';
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const span = max - min || 1;
  const step = vals.length > 1 ? width / (vals.length - 1) : 0;
  let d = '';
  let pen = false;
  vals.forEach((v, i) => {
    if (v == null || !Number.isFinite(v)) {
      pen = false;
      return;
    }
    const x = Math.round(i * step * 10) / 10;
    const y = Math.round((height - 2 - ((v - min) / span) * (height - 4)) * 10) / 10;
    d += `${pen ? 'L' : 'M'}${x} ${y} `;
    pen = true;
  });
  return d.trim();
}
